import {
  API_PATHS,
  SEGMENT_POLL_INTERVAL_MS,
  SEGMENT_POLL_MAX_ATTEMPTS,
  USE_MOCK,
} from '../config';
import { request } from './client';
import { mockSubmitSegmentTask } from '../mock/segmentMock';
import { pollSegmentTask } from './segmentPoll';
import { subscribeSegmentTaskEvents } from './segmentSse';
import { unwrapApiData, normalizeSegmentList } from '../utils/apiResponse';

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

import { COVER_EPISODE_MODES } from '../constants/coverEpisodeModes';
import { normalizeHexColor, normalizeStrokeHexColor } from '../utils/color';

function buildSubmitBody(params) {
  const body = {
    headTrimSeconds: params.headTrimSeconds || 0,
    tailTrimSeconds: params.tailTrimSeconds || 0,
  };
  if (params.mode === 'FIXED_COUNT') {
    body.segmentCount = params.segmentCount;
  } else {
    body.durationSeconds = params.durationSeconds;
  }
  if (params.cover && params.cover.enabled) {
    body.cover = {
      enabled: true,
      templateObjectKey: params.cover.templateObjectKey,
      title: params.cover.title || '',
      episodeMode: params.cover.episodeAuto
        ? COVER_EPISODE_MODES.AUTO_INDEX
        : COVER_EPISODE_MODES.NONE,
      textColor: normalizeHexColor(params.cover.textColor || '#FFFFFF'),
      textStroke: params.cover.textStroke !== false,
      textStrokeColor: normalizeStrokeHexColor(params.cover.textStrokeColor),
      fontSize: params.cover.fontSize,
    };
  }
  if (params.watermark && params.watermark.enabled) {
    body.watermark = {
      enabled: true,
      text: params.watermark.text || '',
      textColor: normalizeHexColor(params.watermark.textColor || '#FFFFFF'),
      fontSize: params.watermark.fontSize,
      position: params.watermark.position || 'BOTTOM_RIGHT',
      textStroke: params.watermark.textStroke !== false,
      textStrokeColor: normalizeStrokeHexColor(params.watermark.textStrokeColor),
    };
  }
  if (params.publish && params.publish.enabled) {
    body.publish = {
      hashtags: params.publish.hashtags || undefined,
      descriptionTemplate: params.publish.descriptionTemplate || undefined,
    };
  }
  return body;
}

function normalizeTaskResult(data) {
  return {
    taskId: data.taskId,
    projectId: data.projectId,
    status: data.status,
    coverEnabled: !!data.coverEnabled,
    coverTitle: data.coverTitle || '',
    publishHashtags: data.publishHashtags || '',
    publishDescriptionTemplate: data.publishDescriptionTemplate || '',
    segments: normalizeSegmentList(data.segments || [], !!data.coverEnabled),
  };
}

function waitTaskWithSse(taskId, options = {}) {
  const { onProgress } = options;
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe = null;

    const finish = (handler, value) => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      handler(value);
    };

    const handleProgress = (data) => {
      if (onProgress) {
        onProgress({
          status: data.status,
          progressPercent: data.progressPercent,
          progressMessage: data.progressMessage,
        });
      }
      if (data.status === 'SUCCESS') {
        finish(resolve, normalizeTaskResult(data));
      } else if (data.status === 'FAILED' || data.status === 'CANCELLED') {
        finish(reject, new Error(data.errorMessage || data.progressMessage || '切片任务失败'));
      }
    };

    unsubscribe = subscribeSegmentTaskEvents(taskId, handleProgress);
    unsubscribe.onError = async () => {
      try {
        const result = await pollSegmentTask(taskId, options);
        finish(resolve, result);
      } catch (error) {
        finish(reject, error);
      }
    };

    request(API_PATHS.segmentTask(taskId))
      .then((payload) => handleProgress(unwrapApiData(payload)))
      .catch((error) => finish(reject, error));
  });
}

export async function submitSegmentTask(projectId, params, options = {}) {
  if (USE_MOCK) {
    return mockSubmitSegmentTask(projectId, params.durationSeconds || 60, options);
  }
  const path = params.mode === 'FIXED_COUNT'
    ? API_PATHS.fixedCount(projectId)
    : API_PATHS.fixedDuration(projectId);
  const payload = await request(path, {
    method: 'POST',
    body: buildSubmitBody(params),
  });
  const data = unwrapApiData(payload);
  if (options.preferSse !== false) {
    return waitTaskWithSse(data.taskId, options);
  }
  return pollSegmentTask(data.taskId, options);
}

export async function submitFixedDurationSegment(projectId, durationSeconds, options = {}) {
  return submitSegmentTask(projectId, {
    mode: 'FIXED_DURATION',
    durationSeconds,
    headTrimSeconds: options.headTrimSeconds || 0,
    tailTrimSeconds: options.tailTrimSeconds || 0,
  }, options);
}

export async function submitHighlightTask(projectId, params, options = {}) {
  if (USE_MOCK) {
    return mockSubmitSegmentTask(projectId, params.durationSeconds || 30, options);
  }
  const body = {
    durationSeconds: params.durationSeconds,
    candidateCount: params.candidateCount,
    liveCategory: params.liveCategory || 'GAME',
    danmakuObjectKey: params.danmakuObjectKey || undefined,
  };
  if (params.cover && params.cover.enabled !== false) {
    body.cover = { enabled: true };
  } else if (params.cover && params.cover.enabled === false) {
    body.cover = { enabled: false };
  }
  if (params.watermark && params.watermark.enabled) {
    body.watermark = {
      enabled: true,
      text: params.watermark.text || '',
      textColor: normalizeHexColor(params.watermark.textColor || '#FFFFFF'),
      fontSize: params.watermark.fontSize,
      position: params.watermark.position || 'BOTTOM_RIGHT',
      textStroke: params.watermark.textStroke !== false,
      textStrokeColor: normalizeStrokeHexColor(params.watermark.textStrokeColor),
    };
  }
  if (params.publish && params.publish.enabled) {
    body.publish = {
      hashtags: params.publish.hashtags || undefined,
      descriptionTemplate: params.publish.descriptionTemplate || undefined,
    };
  }
  const payload = await request(API_PATHS.highlights(projectId), {
    method: 'POST',
    body,
  });
  const data = unwrapApiData(payload);
  if (options.preferSse !== false) {
    return waitTaskWithSse(data.taskId, options);
  }
  return pollSegmentTask(data.taskId, options);
}

export async function uploadDanmakuFile(projectId, file) {
  if (USE_MOCK) {
    return { objectKey: `mock/danmaku/${file.name}`, url: '' };
  }
  const formData = new FormData();
  formData.append('file', file);
  const payload = await request(API_PATHS.projectDanmaku(projectId), {
    method: 'POST',
    body: formData,
  });
  return unwrapApiData(payload);
}

export async function refineHighlightSegment(taskId, segmentId, { startMs, endMs }) {
  if (USE_MOCK) {
    return {
      id: segmentId,
      startMs,
      endMs,
      durationSeconds: Math.max(1, Math.round((endMs - startMs) / 1000)),
      userRefined: true,
    };
  }
  const payload = await request(API_PATHS.segmentRefine(taskId, segmentId), {
    method: 'POST',
    body: { startMs, endMs },
  });
  return unwrapApiData(payload);
}

export async function submitHighlightSegmentFeedback(taskId, segmentId, { feedbackType, note }) {
  if (USE_MOCK) {
    return null;
  }
  const payload = await request(API_PATHS.segmentFeedback(taskId, segmentId), {
    method: 'POST',
    body: { feedbackType, note: note || undefined },
  });
  return unwrapApiData(payload);
}

export async function fetchSegmentTask(taskId) {
  const payload = await request(API_PATHS.segmentTask(taskId));
  return unwrapApiData(payload);
}

export async function resumeSegmentTask(taskId, options = {}) {
  if (USE_MOCK) {
    return pollSegmentTask(taskId, options);
  }
  const data = await fetchSegmentTask(taskId);
  if (data.status === 'SUCCESS') {
    return normalizeTaskResult(data);
  }
  if (data.status === 'FAILED' || data.status === 'CANCELLED') {
    throw new Error(data.errorMessage || data.progressMessage || '切片任务失败');
  }
  return waitTaskWithSse(taskId, options);
}

export async function updatePublishMeta(taskId, payload) {
  if (USE_MOCK) {
    return payload;
  }
  const body = await request(API_PATHS.segmentTaskPublishMeta(taskId), {
    method: 'PUT',
    body: {
      hashtags: payload.hashtags || '',
      descriptionTemplate: payload.descriptionTemplate || '',
    },
  });
  return unwrapApiData(body);
}
